import React, { useState, useEffect } from "react";
import { X, DollarSign, Calendar, Info, CheckCircle } from "lucide-react";
import axios from "axios";
import { MyDomain } from "../../../App";
import moment from "moment";

export default function BossCreatePlacesGloballyPaidModal({
  visible,
  onClose,
  onSuccess,
  placeId,
  placeName,
}) {
  const [userData, setUserData] = useState(null);
  const [profit, setProfit] = useState("");
  const [dateGetPaid, setDateGetPaid] = useState(moment().format("DD-MM-YYYY"));
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load userData from localStorage
  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) setUserData(JSON.parse(user));
  }, []);

  // Reset form when modal opens
  useEffect(() => {
    if (visible) {
      setProfit("");
      setDateGetPaid(moment().format("DD-MM-YYYY"));
      setIsSubmitting(false);
    }
  }, [visible]);

  // Auto-format date input as DD-MM-YYYY
  const handleDateChange = (value) => {
    let cleaned = value.replace(/[^0-9-]/g, "");

    if (cleaned.length >= 2 && !cleaned.includes("-")) {
      cleaned = cleaned.slice(0, 2) + "-" + cleaned.slice(2);
    }
    if (cleaned.length >= 5 && cleaned.split("-").length === 2) {
      const parts = cleaned.split("-");
      cleaned = parts[0] + "-" + parts[1].slice(0, 2) + "-" + parts[1].slice(2);
    }

    if (cleaned.length <= 10) setDateGetPaid(cleaned);
  };

  const handleSubmit = async () => {
    if (!profit || profit.trim() === "") {
      alert("Please enter profit amount");
      return;
    }
    const profitNum = parseFloat(profit);
    if (isNaN(profitNum) || profitNum <= 0) {
      alert("Please enter a valid profit amount");
      return;
    }
    if (!dateGetPaid || dateGetPaid.trim() === "") {
      alert("Please enter a date");
      return;
    }
    if (!userData) {
      alert("User data not found");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${MyDomain}PlacesGloballyPaid/CreatePlacesGloballyPaid`,
        {
          UserId: userData.id,
          PlaceId: placeId,
          Profit: profitNum,
          DateGetPaid: dateGetPaid,
        },
      );

      alert("Global payment added successfully!");
      if (onSuccess) onSuccess(response.data);
      onClose();
    } catch (error) {
      console.error("❌ Error creating global payment:", error);
      alert("Failed to add global payment");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[90vh] flex flex-col animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-6 h-6 text-green-500" />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-gray-900">
                Add Global Payment
              </h2>
              <p className="text-sm text-gray-500 truncate">{placeName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition flex-shrink-0 ml-3"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* ── Form ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Profit */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Profit Amount <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus-within:border-green-400 transition">
              <DollarSign className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <input
                type="number"
                value={profit}
                onChange={(e) => setProfit(e.target.value)}
                placeholder="Enter profit amount"
                className="flex-1 bg-transparent outline-none text-gray-900 text-base"
              />
              <span className="text-green-500 font-semibold ml-2">₪</span>
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Date Received <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus-within:border-blue-400 transition">
              <Calendar className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
              <input
                type="text"
                value={dateGetPaid}
                onChange={(e) => handleDateChange(e.target.value)}
                placeholder="DD-MM-YYYY"
                maxLength={10}
                className="flex-1 bg-transparent outline-none text-gray-900 text-base"
              />
            </div>
            <p className="text-xs text-gray-400 mt-1.5 ml-1">
              Format: DD-MM-YYYY
            </p>
          </div>

          {/* Info box */}
          <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800 leading-relaxed">
              This records a payment received for the global profit of this
              place. It will be tracked against the total agreed profit.
            </p>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex gap-3 px-6 py-5 border-t border-gray-100">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3.5 rounded-xl transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold transition ${
              isSubmitting
                ? "bg-gray-300 text-gray-400 cursor-not-allowed"
                : "bg-green-500 hover:bg-green-600 text-white shadow-md"
            }`}
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Adding...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>Add Payment</span>
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin   { animation: spin 1s linear infinite; }
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type="number"] { -moz-appearance: textfield; }
      `}</style>
    </div>
  );
}
